import { UserResponse } from '@pb/mocks'
import { LoadingStates } from '@pb/utils'
import { pathOr } from 'ramda'

import { AccountSettingsProps } from 'App/duck/types/settings'

import { PaymentCard, NotificationType, CardMemberId } from '../types/payments'
import { UserSettingsType, PersonalIdType, BankNameList } from './types'

const getAppState = (state): any => state.app
const getUserState = (state): UserResponse => getAppState(state).user
const getSettingsState = (state): UserSettingsType => state.userSettings
const getSettings = (state): AccountSettingsProps => getAppState(state).settings

const getAddressBook = (state): UserSettingsType['addressBook'] =>
  getSettingsState(state)?.addressBook
const getAddressBookStatus = (state): UserSettingsType['addressBook']['status'] =>
  getAddressBook(state).status

const getAddressBookLoading = (state): boolean => getAddressBookStatus(state).loading
const getAddressBookSuccess = (state): boolean => getAddressBookStatus(state).success
const getAddressBookErrors = (state): any => getAddressBookStatus(state).errors

const getAddressBookBillingAddress = (state): UserSettingsType['addressBook']['billingAddress'] =>
  getAddressBook(state).billingAddress
const getAddressBookShippingAddresses = (
  state,
): UserSettingsType['addressBook']['shippingAddresses'] => getAddressBook(state).shippingAddresses
const getAddressBookVerifiedAddresses = (
  state,
): UserSettingsType['addressBook']['verifiedAddresses'] => getAddressBook(state).verifiedAddresses

const getAlternativeId = (state): UserSettingsType['alternativeId'] =>
  getSettingsState(state).alternativeId

const getAltIdItems = (state): UserSettingsType['alternativeId']['items'] =>
  getAlternativeId(state).items
const getAltIdMethod = (state): UserSettingsType['alternativeId']['method'] =>
  getAlternativeId(state).method
const getAltIdStatus = (state): UserSettingsType['alternativeId']['status'] =>
  getAlternativeId(state).status
const getAltIdErrors = (state): UserSettingsType['alternativeId']['errors'] =>
  getAlternativeId(state).errors
const getApiErrors = (state): boolean => getSettingsState(state).isApiError
const getIsAltIdItemsFetched = (state): boolean =>
  (getAltIdStatus(state) === LoadingStates.LoadedSuccess ||
    getAltIdStatus(state) === LoadingStates.LoadedFail) &&
  getAltIdMethod(state) === 'GET'
const getIsAltIdItemsLoading = (state): boolean =>
  getAltIdStatus(state) === LoadingStates.Loading && getAltIdMethod(state) === 'GET'
const getIsAltIdItemsError = (state): boolean =>
  getAltIdStatus(state) === LoadingStates.LoadedFail && getAltIdMethod(state) === 'GET'

const getIsAltIdAddCompleted = (state): boolean =>
  getAltIdStatus(state) === LoadingStates.LoadedSuccess && getAltIdMethod(state) === 'ADD'
const getIsAltIdAddLoading = (state): boolean =>
  getAltIdStatus(state) === LoadingStates.Loading && getAltIdMethod(state) === 'ADD'
const getIsAltIdAddError = (state): boolean =>
  getAltIdStatus(state) === LoadingStates.LoadedFail && getAltIdMethod(state) === 'ADD'

const getIsAltIdDeleteCompleted = (state): boolean =>
  getAltIdStatus(state) === LoadingStates.LoadedSuccess && getAltIdMethod(state) === 'DELETE'
const getIsAltIdDeleteLoading = (state): boolean =>
  getAltIdStatus(state) === LoadingStates.Loading && getAltIdMethod(state) === 'DELETE'
const getIsAltIdDeleteError = (state): boolean =>
  getAltIdStatus(state) === LoadingStates.LoadedFail && getAltIdMethod(state) === 'DELETE'

const showEmailConfirmationSuccessNotification = (state): boolean =>
  getSettingsState(state).showEmailConfirmationSuccessNotification
const showEmailConfirmationFailureNotification = (state): boolean =>
  getSettingsState(state).showEmailConfirmationFailureNotification

const getUserHasPrintedReceipts = (state): boolean => getUserState(state).printReceipt

const showSettingsUpdateSuccessNotification = (state): boolean =>
  getSettingsState(state).showSettingsUpdateSuccessNotification
const showSettingsUpdateFailureNotification = (state): boolean =>
  getSettingsState(state).showSettingsUpdateFailureNotification

const settingsUpdateNotificationMessage = (state): string =>
  getSettingsState(state).settingsUpdateNotificationMessage

const getIsPayLaterDeactivateLoading = (state): boolean =>
  pathOr(false, ['payLaterDeactivate', 'isLoading'], getSettingsState(state))
const getHasPayLaterDeactivateFailed = (state): boolean =>
  pathOr(false, ['payLaterDeactivate', 'hasFailed'], getSettingsState(state))

const getUserHasRequestedData = (state): boolean => getUserState(state).apcustomerRequested
const getUserHasPhoneAltId = (state): boolean => getUserState(state).altIdSaved

const getIsChangePasswordLoading = (state): boolean =>
  pathOr(false, ['changePassword', 'isLoading'], getSettingsState(state))

const getIsChangePasswordFailed = (state): boolean =>
  pathOr(false, ['changePassword', 'hasFailed'], getSettingsState(state))

const getIsChangePasswordSuccess = (state): boolean =>
  pathOr(false, ['changePassword', 'success'], getSettingsState(state))

const getChangePasswordErrors = (state): any =>
  pathOr('', ['changePassword', 'errors'], getSettingsState(state))

const getIsDeleteAccountLoading = (state): boolean =>
  pathOr(false, ['deleteAccount', 'isLoading'], getSettingsState(state))

const getIsDeleteAccountFailed = (state): boolean =>
  pathOr(false, ['deleteAccount', 'hasFailed'], getSettingsState(state))

const getDeleteAccountError = (state): any =>
  pathOr(false, ['deleteAccount', 'error'], getSettingsState(state))

const showSettingsDeleteFailureNotification = (state): boolean =>
  getSettingsState(state).showSettingsDeleteFailureNotification

const settingsDeleteNotificationMessage = (state): string =>
  getSettingsState(state).settingsDeleteNotificationMessage

const getIsDeleteAccountSuccess = (state): boolean =>
  pathOr(false, ['deleteAccount', 'success'], getSettingsState(state))

const getIsUpdateUserDetailsLoading = (state): boolean =>
  pathOr(false, ['updateUserDetails', 'isLoading'], getSettingsState(state))

const getIsUpdateUserDetailsFailed = (state): boolean =>
  pathOr(false, ['updateUserDetails', 'hasFailed'], getSettingsState(state))

const getIsUpdateCommunityNicknameNotLoaded = (state): boolean =>
  getSettingsState(state)?.updateCommunityNickname?.status === LoadingStates.NotLoaded

const getIsUpdateCommunityNicknameLoading = (state): boolean =>
  getSettingsState(state)?.updateCommunityNickname?.status === LoadingStates.Loading

const getIsUpdateCommunityNicknameLoadedSuccess = (state): boolean =>
  getSettingsState(state)?.updateCommunityNickname?.status === LoadingStates.LoadedSuccess

const getIsUpdateCommunityNicknameFailed = (state): boolean =>
  getSettingsState(state)?.updateCommunityNickname?.status === LoadingStates.LoadedFail

const getUpdateUserDetailsErrors = (state): boolean =>
  pathOr(false, ['updateUserDetails', 'errors'], getSettingsState(state))

const getProvinceList = (state): Record<string, string> =>
  getAppState(state)?.settings?.provinceList

const getSelectedProvince = (state): string => getAddressBook(state)?.selectedProvince

const getSelectedProvinceCode = (state): string => getAddressBook(state)?.selectedProvinceCode

const getCitiesListByProvince = (state, province): Object => getAddressBook(state)?.cities[province]

const getShouldFetchCitiesForProvince = (state, province): boolean =>
  !getAddressBook(state).cities[province]

const getSelectedCity = (state): string => getAddressBook(state)?.selectedCity

const getDistrictsListByCity = (state, city): Object => getAddressBook(state)?.districts[city]

const getShouldFetchDistrictsForCity = (state, city): boolean =>
  !getAddressBook(state).districts[city]

const getIsDistrictsLoading = (state): boolean =>
  getAddressBook(state).districtsLoadingState === LoadingStates.Loading

const getIsCitiesLoading = (state): boolean =>
  getAddressBook(state).citiesLoadingState === LoadingStates.Loading

const getShouldFetchCities = (state): boolean => !!getAppState(state)?.settings?.provinceList

const getShouldFetchDistricts = (state): boolean =>
  getAddressBook(state).citiesLoadingState !== LoadingStates.NotLoaded &&
  Object.keys(getAddressBook(state).cities).length > 0

const getPostalCode = (state): string => getAddressBook(state)?.postalCode

const getPaymentsState = (state): UserSettingsType['payments'] => getSettingsState(state)?.payments
const getPaymentCards = (state): PaymentCard[] => getPaymentsState(state)?.paymentInfoData || []
const getBankAccountDetails = (state): UserSettingsType['payments']['bankAccountDetail'] =>
  getPaymentsState(state)?.bankAccountDetail

const getBankAccountType = (state): UserSettingsType['payments']['accountTypeList'] =>
  getPaymentsState(state)?.accountTypeList
const getUpdateBankDetailsErrors = (state): UserSettingsType['payments']['errors'] =>
  getPaymentsState(state).errors

const getInitialPaymentState = (state): LoadingStates =>
  getPaymentsState(state).paymentsLoadingState
const getIsPaymentsLoading = (state): boolean =>
  getPaymentsState(state).paymentsLoadingState === LoadingStates.Loading
const getIsPaymentsLoadedSuccess = (state): boolean =>
  getPaymentsState(state).paymentsLoadingState === LoadingStates.LoadedSuccess
const getIsPaymentsLoadedFail = (state): boolean =>
  getPaymentsState(state).paymentsLoadingState === LoadingStates.LoadedFail

const getAddBankAccountLoading = (state): boolean =>
  getPaymentsState(state).status === LoadingStates.Loading
const getAddBankAccountSuccess = (state): boolean =>
  getPaymentsState(state).status === LoadingStates.LoadedSuccess
const getAddBankAccountFail = (state): boolean =>
  getPaymentsState(state).status === LoadingStates.LoadedFail

const getRemovedPaymentLoading = (state): boolean =>
  getPaymentsState(state).removePaymentLoadingState === LoadingStates.Loading

const getPaymentsNotification = (state): NotificationType | null =>
  getPaymentsState(state).paymentsNotification

const getPaymentCardMemberIds = (state): CardMemberId[] =>
  getPaymentsState(state).paymentCardMemberIds
const getIsNavigateToReactPaymentPage = (state): boolean => !getSettings(state)?.cbpEnabled
const getPaymentDecouplingEnabled = (state): boolean => getSettings(state)?.paymentDecouplingEnabled

const showBankAccountForm = (state): boolean => getPaymentsState(state)?.showBankAccountForm
const getIsAddNewCardLoading = (state): boolean =>
  getPaymentsState(state).addNewCard.status === LoadingStates.Loading
const getIsAddNewCardSuccess = (state): boolean =>
  getPaymentsState(state).addNewCard.status === LoadingStates.LoadedSuccess
const getIsAddNewCardFail = (state): boolean =>
  getPaymentsState(state).addNewCard.status === LoadingStates.LoadedFail
const getAddNewCardErrors = (state): UserSettingsType['payments']['addNewCard']['errors'] =>
  getPaymentsState(state).addNewCard.errors

const getUserBirthDateISO = (state): string => {
  const { day, month, year } = getPaymentsState(state)?.bankAccountDetail
  if (day && month && year) {
    return `${year}-${month}-${day}`
  }

  return ''
}

const getCustomerCorePortalEnabled = (state): boolean =>
  getAppState(state)?.settings?.customerCorePortalEnabled

const getCustomerCorePortalEncryptionEnabled = (state): boolean =>
  getAppState(state)?.settings?.customerCorePortalEncryptionEnabled

const getCustomerCorePortalLink = (
  state,
  type: 'customerCorePortalLink' | 'customerCorePortalUrl',
): { name: string; url: string } | undefined =>
  getAppState(state)?.settings?.privacy?.find((p) => p.name === type)

const getDefaultCardConfig = (state): UserSettingsType['payments']['defaultCardConfig'] =>
  getPaymentsState(state).defaultCardConfig

const getNewsSubscription = (state): boolean => getSettingsState(state)?.newsLetterSubscription

const getIsTokySSOEnabled = (state): boolean => getSettings(state)?.tokySSOEnabled
const getUserCommunityNickname = (state): string | undefined =>
  getUserState(state)?.communityNickname
const getUserCommunityNicknameRedirectUrl = (state): string | undefined =>
  getSettingsState(state)?.updateCommunityNickname?.redirectUrl
const getUserCommunityNicknameUpdated = (state): string | undefined =>
  getSettingsState(state)?.updateCommunityNickname?.communityNickname

const getShowCompleteProfileReminder = (state): boolean =>
  getSettingsState(state)?.showCompleteProfileReminder

const getEmailEditable = (state): boolean => getSettings(state).emailEditable

const getPersonalIdType = (state): PersonalIdType => getSettings(state).personalIdTypeMap

const getBankNameList = (state): BankNameList => getSettings(state).bankNameMap

const getBranchListByBank = (state): Object => getPaymentsState(state)?.branchList

const getShouldFetchBranch = (state): boolean => !!getSettings(state).bankNameMap
const getBranchListLength = (state): number =>
  Object.keys(getPaymentsState(state)?.branchList).length

const showPhoneAsAltIdAddedNotification = (state): boolean =>
  getSettingsState(state).showPhoneAsAltIdAddedNotification

const showPhoneAsAltIdRemovedNotification = (state): boolean =>
  getSettingsState(state).showPhoneAsAltIdRemovedNotification

export default {
  getCustomerCorePortalEnabled,
  getCustomerCorePortalEncryptionEnabled,
  getCustomerCorePortalLink,
  showEmailConfirmationSuccessNotification,
  showEmailConfirmationFailureNotification,
  getUserHasPrintedReceipts,
  showSettingsUpdateSuccessNotification,
  showSettingsUpdateFailureNotification,
  settingsUpdateNotificationMessage,
  getIsPayLaterDeactivateLoading,
  getHasPayLaterDeactivateFailed,
  getUserHasRequestedData,
  getIsChangePasswordLoading,
  getIsChangePasswordFailed,
  getIsChangePasswordSuccess,
  getIsDeleteAccountLoading,
  getIsDeleteAccountFailed,
  getIsDeleteAccountSuccess,
  getDeleteAccountError,
  getChangePasswordErrors,
  getIsUpdateUserDetailsLoading,
  getIsUpdateUserDetailsFailed,
  getIsUpdateCommunityNicknameNotLoaded,
  getIsUpdateCommunityNicknameLoadedSuccess,
  getIsUpdateCommunityNicknameLoading,
  getIsUpdateCommunityNicknameFailed,
  getUpdateUserDetailsErrors,
  getAddressBook,
  getAddressBookLoading,
  getAddressBookSuccess,
  getAddressBookErrors,
  getAddressBookBillingAddress,
  getAddressBookShippingAddresses,
  getAddressBookVerifiedAddresses,
  getAlternativeId,
  getAltIdItems,
  getAltIdMethod,
  getAltIdStatus,
  getAltIdErrors,
  getApiErrors,
  getIsAltIdItemsFetched,
  getIsAltIdItemsLoading,
  getIsAltIdItemsError,
  getIsAltIdAddCompleted,
  getIsAltIdAddLoading,
  getIsAltIdAddError,
  getIsAltIdDeleteCompleted,
  getIsAltIdDeleteLoading,
  getIsAltIdDeleteError,
  settingsDeleteNotificationMessage,
  showSettingsDeleteFailureNotification,
  getProvinceList,
  getSelectedProvince,
  getSelectedProvinceCode,
  getCitiesListByProvince,
  getShouldFetchCitiesForProvince,
  getSelectedCity,
  getShouldFetchCities,
  getDistrictsListByCity,
  getShouldFetchDistrictsForCity,
  getShouldFetchDistricts,
  getIsDistrictsLoading,
  getIsCitiesLoading,
  getPostalCode,
  getPaymentsState,
  getPaymentCards,
  getIsPaymentsLoading,
  getIsPaymentsLoadedSuccess,
  getIsPaymentsLoadedFail,
  getUpdateBankDetailsErrors,
  getPaymentsNotification,
  getPaymentCardMemberIds,
  getIsNavigateToReactPaymentPage,
  getPaymentDecouplingEnabled,
  showBankAccountForm,
  getIsAddNewCardLoading,
  getIsAddNewCardSuccess,
  getIsAddNewCardFail,
  getAddNewCardErrors,
  getRemovedPaymentLoading,
  getBankAccountDetails,
  getBankAccountType,
  getInitialPaymentState,
  getDefaultCardConfig,
  getNewsSubscription,
  getIsTokySSOEnabled,
  getUserCommunityNickname,
  getUserCommunityNicknameRedirectUrl,
  getUserCommunityNicknameUpdated,
  getShowCompleteProfileReminder,
  getEmailEditable,
  getUserBirthDateISO,
  getAddBankAccountLoading,
  getAddBankAccountSuccess,
  getAddBankAccountFail,
  getPersonalIdType,
  getBankNameList,
  getBranchListByBank,
  getShouldFetchBranch,
  getBranchListLength,
  getUserHasPhoneAltId,
  showPhoneAsAltIdAddedNotification,
  showPhoneAsAltIdRemovedNotification,
}
